import type { Maybe } from '@adornis/base/utilTypes.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { computed } from '@lit-labs/preact-signals';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { GroupsAPI } from '../../../groups/api/GroupsAPI.js';
import { School } from '../../../groups/entities/School.js';
import { getSchoolIDsForRoles } from '../../../permissions/api/getSchoolIDsForRoles.js';
import { Permission, Role } from '../../../permissions/roles.js';
import { InterviewAPI } from '../api/InterviewAPI.js';
import { handleGenerateInterviewTimeslots } from './handle-generate-interview-timeslots.js';
import { sgInterviewDetailDialog } from './sg-interview-detail-dialog.js';
import { PageHeader } from '@martinus/styles/client/PageHeader.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { EmptyState } from '@adornis/components/client/EmptyState.js';

export interface MappedUITimeSlot {
  id: string;
  day: string | undefined;
  date: string | undefined;
  time: string;
  studentOccupied: boolean;
  interviewerOccupied: boolean;
  schoolID: Maybe<string>;
}

const renderSlot = (time: string, slots: MappedUITimeSlot[]) => {
  const availableStudentSlots = slots.filter(s => !s.studentOccupied);
  const availableInterviewerSlots = slots.filter(s => !s.interviewerOccupied);

  return html`
    <div
      class="card cursor-pointer hover:bg-base-200 transition-all p-3"
      @click=${() => sgInterviewDetailDialog(slots)}
    >
      <div class="flex flex-col gap-4">
        <div class="font-semibold text-sm">${time}</div>
        <div class="flex flex-col gap-2 text-sm">
          <div class="flex justify-between items-center gap-1">
            <span
              class="${
                availableStudentSlots.length > 0 && availableInterviewerSlots.length > 0
                  ? 'text-success'
                  : 'text-warning'
              } font-medium"
            >
              ${availableStudentSlots.length} Schülerplätze frei
            </span>
            <span>${slots.length - availableStudentSlots.length}/${slots.length}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="${availableInterviewerSlots.length > 0 ? 'text-error' : 'text-success'} font-medium">
              ${
                availableInterviewerSlots.length > 0
                  ? `${availableInterviewerSlots.length} Gesprächspartnerplätze nicht besetzt`
                  : 'Alle Plätze zugewiesen'
              }
            </span>
            <span>${slots.length - availableInterviewerSlots.length}/${slots.length}</span>
          </div>
        </div>
      </div>
    </div>
  `;
};

const renderDayColumn = (day: string, slots: Record<string, MappedUITimeSlot[]>) => html`
  <div class="flex flex-col gap-3">
    <div class="text-center">
      <h3 class="font-semibold">${day}</h3>
    </div>
    <div class="flex flex-col gap-2">${Object.keys(slots).map(time => renderSlot(time, slots[time] ?? []))}</div>
  </div>
`;

export const SGInterviewSlotAdministration = stateful(() => {
  const user = useObservable(() => currentUser(), []);
  const [schoolOfUser, setSchoolOfUser] = useState<string | null>(null);

  // for schulleiter we only want to admin the slots of the schools they are assigned to
  useEffect(() => {
    void getSchoolIDsForRoles(user, [Role.SCHULLEITER, Role.SUS_BEWERBER_BEARBEITUNG]).then(schools => {
      setSchoolOfUser(schools[0] ?? null);
    });
  }, [user?.roles]);

  const { value: slots, loading: slotsLoading } = useQuery(
    () =>
      InterviewAPI.subscribeAll(
        schoolOfUser ? [schoolOfUser] : [],
        true,
      )({
        _id: 1,
        _class: 1,
        schoolID: 1,
        startDate: 1,
        endDate: 1,
        childID: 1,
        interviewerID: 1,
      }),
    // the school of a Schulleiter arrives asynchronously; without it as a dependency the query never narrowed to it
    [schoolOfUser],
  );

  const { value: schools, loading: schoolsLoading } = useQuery(
    () =>
      GroupsAPI.subscribeGroups(
        0,
        30,
        new AdornisFilter({
          aspects: [
            new StringAspect({ fieldName: '_class', value: School._class, mode: 'equals' }),
            new BooleanAspect({ fieldName: 'isSgSchool', value: true }),
          ],
        }),
      )({
        _id: 1,
        name: 1,
      }),
    [],
  );

  const timeSlots = computed(() => {
    if (slotsLoading || !slots) {
      return [];
    }

    return slots.map(slot => {
      const startDate = slot.startDate;
      const endDate = slot.endDate;
      return {
        id: slot._id,
        day: startDate ? format(startDate, 'EEEE', { locale: de }) : undefined,
        date: startDate ? format(startDate, 'dd.MM.yyyy') : undefined,
        time: `${startDate ? format(startDate, 'HH:mm') : ''} - ${endDate ? format(endDate, 'HH:mm') : ''}`,
        studentOccupied: !!slot.childID,
        interviewerOccupied: !!slot.interviewerID,
        schoolID: slot.schoolID,
      };
    });
  });

  // group by time and date
  const groupedSlots = computed(() =>
    timeSlots.value.reduce<Record<string, Record<string, Record<string, MappedUITimeSlot[]>>>>((groups, slot) => {
      if (!slot.date || !slot.time || !slot.schoolID) return groups;
      const dayHash = slot.date;
      const timeHash = slot.time;
      const schoolHash = slot.schoolID;

      const schoolSlots = groups[schoolHash] ?? {};
      const daySlots = schoolSlots[dayHash] ?? {};
      const timeSlots = daySlots[timeHash] ?? [];
      return {
        ...groups,
        [schoolHash]: {
          ...schoolSlots,
          [dayHash]: {
            ...daySlots,
            [timeHash]: [...timeSlots, slot],
          },
        },
      };
    }, {}),
  );

  const allSlots = Array.from(Object.entries(groupedSlots.value));

  const header = PageHeader({
    title: 'SuS Anmeldegespräche',
    rightContent: user?.hasPermission(Permission.ADMINISTRATIVE_SUS_INTERVIEWS)
      ? html`
          <button type="button" class="btn btn-primary" @click=${() => handleGenerateInterviewTimeslots(schoolOfUser)}>
            <span class="icon-[material-symbols--event-available-outline-rounded] size-5"></span>
            Termine generieren
          </button>
        `
      : undefined,
  });

  if (slotsLoading || schoolsLoading) return html`${header}${PageLoader({})}`;

  return html`
    ${header}
    <div class="card-container">
      ${
        allSlots.length === 0
          ? html`<div class="card">
              ${EmptyState({
                icon: 'icon-[material-symbols--event-busy-outline-rounded]',
                title: 'Keine Termine gefunden',
                description: 'Noch sind keine Anmeldegespräche angelegt.',
              })}
            </div>`
          : nothing
      }
      ${repeat(
        allSlots,
        ([schoolID]) => schoolID,
        ([schoolID, schoolSlots]) => {
          const schoolName = schools?.find(s => s._id === schoolID)?.name;
          return html`
            <div class="card w-full p-4 gap-6 flex-grow">
              ${CardHeader({ title: schoolName ?? '' })}
              ${
                Object.entries(schoolSlots).length === 0
                  ? EmptyState({
                      icon: 'icon-[material-symbols--event-busy-outline-rounded]',
                      title: 'Keine Termine gefunden',
                    })
                  : nothing
              }
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                ${Object.entries(schoolSlots).map(([day, slots]) => renderDayColumn(day, slots))}
              </div>
            </div>
          `;
        },
      )}
    </div>
  `;
});
